//@ts-nocheck
"use client"

import { useState, useEffect, use } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Check, Copy, Download, Link as LinkIcon } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@/components/ui/table'
import { AlertCircle, AlertTriangle, ArrowRight, ExternalLink, Info, X } from "lucide-react"
import { imageSize } from "@/lib/files"

export function MarkdownRenderer({ content, images = [] }) {
  const router = useRouter()
  const [copiedCode, setCopiedCode] = useState(null)
  const [processedContent, setProcessedContent] = useState("")
  const [customComponents, setCustomComponents] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogUrl, setDialogUrl] = useState("")

  // Handle copying code
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  // Handle downloading images
  const handleDownloadImage = async (url, filename) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = objectUrl
      link.download = filename || "image.png"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(objectUrl)
    } catch (error) {
      console.error("Error downloading image:", error)
    }
  }

  // Process content to extract custom components and replace them with placeholders
  useEffect(() => {
    const components: any[] = []
    let newContent = content
    const codeBlocks = []
    const codeLines = []

    // First, extract all code blocks and replace them with placeholders
    newContent = newContent.replace(/```([a-z]*)\n([\s\S]*?)```/g, (match, language, code) => {
      const id = `CODE_BLOCK_${codeBlocks.length}`
      codeBlocks.push({ id, language, code })
      return id
    })

    // Extract inline code and replace with placeholders
    newContent = newContent.replace(/`([^`]+)`/g, (match, code) => {
      const id = `CODE_LINE_${codeLines.length}`
      codeLines.push({ id, code })
      return id
    })

    // Extract alert components
    newContent = newContent.replace(/:::(\w+)\s+([\s\S]*?):::/gs, (match, type, content) => {
      // Restore code blocks in the alert content
      let body = content.trim()
      codeBlocks.slice().reverse().forEach(({ id, language, code }) => {
        if (body.includes(id)) {
          body = body.replace(id, `\`\`\`${language}\n${code}\`\`\``)
        }
      })

      codeLines.slice().reverse().forEach(({ id, code }) => {
        if (body.includes(id)) {
          body = body.replace(id, `\`${code}\``)
        }
      })

      components.slice().reverse().forEach(({ id, type, props }) => {
        if (body.includes(id)) {
          switch(type) {
            case 'card': 
              body = body.replace(id, `[[card]]
                ${props.title ? `# ${props.title}` : ''}
                ${props.description ? `> ${props.description}` : ''}
                ${props.content}
                `)
              break
            case 'alert':
              body = body.replace(id, `:::${props.alertType}
                ${props.content}
                :::
              `)
              break
            case 'button':
              body = body.replace(id, `[[button:${props.buttonType} ${props.text}|${props.url ? `url=${props.url}` : props.text ? `text=${props.text}` : ''}]]`) 
              break
            case 'tooltip':
              body = body.replace(id, `[[tooltip:${props.text}|${props.tooltip}]]`)
              break
            case 'popover':
              body = body.replace(id, `[[popover:${props.text}|
                ${props.content}
                ]]`)
              break
            case 'hovercard':
              body = body.replace(id, `[[hovercard:${props.text}|
                ${props.content}
                ]]`)
              break
            case 'carousel':
              body = body.replace(id, `[[carousel]]
                ${props.width ? `![width=${props.width}]` : ''}
                ${props.height ? `![height=${props.height}]` : ''}
                ${props.images?.map(image => `![${image.alt}](${image.src})`)}
                [[/carousel]]`)
              break
          }
        }
      })

      const id = `CUSTOM_COMPONENT_${components.length}`
      components.push({
        id,
        type: "alert",
        props: {
          alertType: type.toLowerCase(),
          content: body,
        },
      })
      return id
    })

    // Extract button components
    newContent = newContent.replace(/\[\[button:(\w+)\s+(.*?)\]\]/g, (match, type, content) => {
      const [text, ...rest] = content.split("|")
      const props = {
        buttonType: type,
        text: text.trim(),
      }

      rest.forEach((prop) => {
        const [key, value] = prop.split("=")
        if (key && value) {
          props[key.trim()] = value.trim()
        }
      })

      const id = `CUSTOM_COMPONENT_${components.length}`
      components.push({
        id,
        type: "button",
        props,
      })
      return id
    })

    // Extract tooltip components
    newContent = newContent.replace(/\[\[tooltip:\s*(.*?)\|\s*(.*?)\]\]/g, (match, text, tooltip) => {
      const id = `CUSTOM_COMPONENT_${components.length}`
      components.push({
        id,
        type: "tooltip",
        props: {
          text: text.trim(),
          tooltip: tooltip.trim(),
        },
      })
      return id
    })

    // Extract popover components - Fix the regex to properly capture multiline content
    newContent = newContent.replace(/\[\[popover:\s*(.*?)\|\s*([\s\S]*?)\]\]/g, (match, text, content) => {
      // Restore code blocks in the popover content
      let body = content.trim()
      codeBlocks.slice().reverse().forEach(({ id, language, code }) => {
        if (body.includes(id)) {
          body = body.replace(id, `\`\`\`${language}\n${code}\`\`\``)
        }
      })

      codeLines.slice().reverse().forEach(({ id, code }) => {
        if (body.includes(id)) {
          body = body.replace(id, `\`${code}\``)
        }
      })

      components.slice().reverse().forEach(({ id, type, props }) => {
        if (body.includes(id)) {
          switch(type) {
            case 'card': 
              body = body.replace(id, `[[card]]
                ${props.title ? `# ${props.title}` : ''}
                ${props.description ? `> ${props.description}` : ''}
                ${props.content}
                `)
              break
            case 'alert':
              body = body.replace(id, `:::${props.alertType}
                ${props.content}
                :::
              `)
              break
            case 'button':
              body = body.replace(id, `[[button:${props.buttonType} ${props.text}|${props.url ? `url=${props.url}` : props.text ? `text=${props.text}` : ''}]]`) 
              break
            case 'tooltip':
              body = body.replace(id, `[[tooltip:${props.text}|${props.tooltip}]]`)
              break
            case 'popover':
              body = body.replace(id, `[[popover:${props.text}|
                ${props.content}
                ]]`)
              break
            case 'hovercard':
              body = body.replace(id, `[[hovercard:${props.text}|
                ${props.content}
                ]]`)
              break
            case 'carousel':
              body = body.replace(id, `[[carousel]]
                ${props.width ? `![width=${props.width}]` : ''}
                ${props.height ? `![height=${props.height}]` : ''}
                ${props.images?.map(image => `![${image.alt}](${image.src})`)}
                [[/carousel]]`)
              break
          }
        }
      })

      const id = `CUSTOM_COMPONENT_${components.length}`
      components.push({
        id,
        type: "popover",
        props: {
          text: text.trim(),
          content: body,
        },
      })
      return id
    })

    // Extract hovercard components - Fix the regex to properly capture multiline content
    // This regex now matches both [[hovercard: and [[hovercard:
    newContent = newContent.replace(/\[\[hovercard:?\s*(.*?)\|\s*([\s\S]*?)\]\]/g, (match, text, content) => {
      // Restore code blocks in the hovercard content
      let body = content.trim()
      codeBlocks.slice().reverse().forEach(({ id, language, code }) => {
        if (body.includes(id)) {
          body = body.replace(id, `\`\`\`${language}\n${code}\`\`\``)
        }
      })

      codeLines.slice().reverse().forEach(({ id, code }) => {
        if (body.includes(id)) {
          body = body.replace(id, `\`${code}\``)
        }
      })

      components.slice().reverse().forEach(({ id, type, props }) => {
        if (body.includes(id)) {
          switch(type) {
            case 'card': 
              body = body.replace(id, `[[card]]
                ${props.title ? `# ${props.title}` : ''}
                ${props.description ? `> ${props.description}` : ''}
                ${props.content}
                `)
              break
            case 'alert':
              body = body.replace(id, `:::${props.alertType}
                ${props.content}
                :::
              `)
              break
            case 'button':
              body = body.replace(id, `[[button:${props.buttonType} ${props.text}|${props.url ? `url=${props.url}` : props.text ? `text=${props.text}` : ''}]]`) 
              break
            case 'tooltip':
              body = body.replace(id, `[[tooltip:${props.text}|${props.tooltip}]]`)
              break
            case 'popover':
              body = body.replace(id, `[[popover:${props.text}|
                ${props.content}
                ]]`)
              break
            case 'hovercard':
              body = body.replace(id, `[[hovercard:${props.text}|
                ${props.content}
                ]]`)
              break
            case 'carousel':
              body = body.replace(id, `[[carousel]]
                ${props.width ? `![width=${props.width}]` : ''}
                ${props.height ? `![height=${props.height}]` : ''}
                ${props.images?.map(image => `![${image.alt}](${image.src})`)}
                [[/carousel]]`)
              break
          }
        }
      })

      const id = `CUSTOM_COMPONENT_${components.length}`
      components.push({
        id,
        type: "hovercard",
        props: {
          text: text.trim(),
          content: body,
        },
      })
      return id
    })

    // Extract carousel components
    newContent = newContent.replace(/\[\[carousel\]\]([\s\S]*?)\[\[\/carousel\]\]/gs, (match, content) => {
      const lines = content.trim().split("\n")
      const carouselImages = []
      let carWidth = 1, carHeight = 1

      // Parse image lines - Fix the regex to properly capture image syntax
      lines.forEach((line) => {
        // Match standard markdown image syntax: ![alt text](url)
        const width = line.match(/!\[width=(\d*?)\]/)?.[1]
        const height = line.match(/!\[height=(\d*?)\]/)?.[1]
        const imageMatch = line.match(/!\[(.*?)\]\((.*?)\)/)

        if (imageMatch) {
          const alt = imageMatch[1]
          const src = imageMatch[2]
          carouselImages.push({ alt, src })
        }

        if (width && width > carWidth)
          carWidth = width
        if(height && height > carHeight)
          carHeight = height
      })

      const id = `CUSTOM_COMPONENT_${components.length}`
      components.push({
        id,
        type: "carousel",
        props: {
          images: carouselImages,
          width: carWidth,
          height: carHeight
        },
      })
      return id
    })

    // Extract card components
    newContent = newContent.replace(/\[\[card\]\]([\s\S]*?)\[\[\/card\]\]/gs, (match, content) => {
      const lines = content.trim().split("\n")
      const title = lines.find((line) => line.startsWith("# "))?.substring(2) || ""
      const description = lines.find((line) => line.startsWith("> "))?.substring(2) || ""

      // Get the body content, excluding title and description lines
      let body = lines.filter((line) => !line.startsWith("# ") && !line.startsWith("> ")).join("\n")

      // Restore code blocks in the card content
      codeBlocks.slice().reverse().forEach(({ id, language, code }) => {
        if (body.includes(id)) {
          body = body.replace(id, `\`\`\`${language}\n${code}\`\`\``)
        }
      })

      codeLines.slice().reverse().forEach(({ id, code }) => {
        if (body.includes(id)) {
          body = body.replace(id, `\`${code}\``)
        }
      })

      components.slice().reverse().forEach(({ id, type, props }) => {
        if (body.includes(id)) {
          switch(type) {
            case 'card': 
              body = body.replace(id, `[[card]]
                ${props.title ? `# ${props.title}` : ''}
                ${props.description ? `> ${props.description}` : ''}
                ${props.content}
                `)
              break
            case 'alert':
              body = body.replace(id, `:::${props.alertType}
                ${props.content}
                :::
              `)
              break
            case 'button':
              body = body.replace(id, `[[button:${props.buttonType} ${props.text}|${props.url ? `url=${props.url}` : props.text ? `text=${props.text}` : ''}]]`) 
              break
            case 'tooltip':
              body = body.replace(id, `[[tooltip:${props.text}|${props.tooltip}]]`)
              break
            case 'popover':
              body = body.replace(id, `[[popover:${props.text}|
                ${props.content}
                ]]`)
              break
            case 'hovercard':
              body = body.replace(id, `[[hovercard:${props.text}|
                ${props.content}
                ]]`)
              break
            case 'carousel':
              body = body.replace(id, `[[carousel]]
                ${props.width ? `![width=${props.width}]` : ''}
                ${props.height ? `![height=${props.height}]` : ''}
                ${props.images?.map(image => `![${image.alt}](${image.src})`)}
                [[/carousel]]`)
              break
          }
        }
      })

      const id = `CUSTOM_COMPONENT_${components.length}`
      components.push({
        id,
        type: "card",
        props: {
          title,
          description,
          content: body,
        },
      })
      return id
    })

    // Restore code blocks in the main content
    codeBlocks.reverse().forEach(({ id, language, code }) => {
      if (newContent.includes(id)) {
        newContent = newContent.replace(id, `\`\`\`${language}\n${code}\`\`\``)
      }
    })

    // Restore inline code in the main content
    codeLines.reverse().forEach(({ id, code }) => {
      if (newContent.includes(id)) {
        newContent = newContent.replace(id, `\`${code}\``)
      }
    })

    setProcessedContent(newContent)
    setCustomComponents(components)
  }, [content])

  // Handle external link click
  const handleExternalLinkClick = (url) => {
    setDialogUrl(url)
    setDialogOpen(true)
  }

  // Render a custom component based on its type and props
  const renderCustomComponent = (component) => {
    const { type, props } = component

    if (type === "alert") {
      const { alertType, content } = props
      const alertIcons = {
        info: <Info className="h-4 w-4" />,
        warning: <AlertTriangle className="h-4 w-4" />,
        error: <AlertCircle className="h-4 w-4" />,
        success: <Check className="h-4 w-4" />,
      }

      const alertVariants = {
        info: "info",
        warning: "warning",
        error: "destructive",
        success: "success",
      }

      return (
        <Alert className="my-6" variant={alertVariants[alertType] || "info"}>
          {alertIcons[alertType] || alertIcons.info}
          <AlertTitle className="mb-1 font-medium">{alertType.charAt(0).toUpperCase() + alertType.slice(1)}</AlertTitle>
          <AlertDescription>
            <MarkdownRenderer content={content} images={images} />
          </AlertDescription>
        </Alert>
      )
    }

    if (type === "button") {
      const { buttonType, text, url } = props
      const buttonIcons = {
        download: <Download className="mr-2 h-4 w-4" />,
        copy: <Copy className="mr-2 h-4 w-4" />,
        link: <LinkIcon className="mr-2 h-4 w-4" />,
      }

      // For link buttons that go to external URLs
      if (buttonType === "link" && url && url.startsWith("http")) {
        return (
          <Button className="my-4" variant="default" onClick={() => handleExternalLinkClick(url)}>
            {buttonIcons[buttonType]}
            {text}
          </Button>
        )
      }

      // For other button types or internal links
      const handleClick = () => {
        if (buttonType === "download" && url) {
          window.open(url, "_blank")
        } else if (buttonType === "copy" && props.text) {
          navigator.clipboard.writeText(props.text)
        } else if (buttonType === "link" && url && !url.startsWith("http")) {
          router.push(url)
        }
      }

      return (
        <Button className="my-4" variant="default" onClick={handleClick}>
          {buttonIcons[buttonType] || buttonIcons.link}
          {text}
        </Button>
      )
    }

    if (type === "tooltip") {
      const { text, tooltip } = props
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help border-b border-dotted border-muted-foreground">{text}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    if (type === "popover") {
      const { text, content } = props
      return (
        <Popover>
          <PopoverTrigger asChild>
            <span className="cursor-pointer border-b border-dotted border-primary text-primary">{text}</span>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <MarkdownRenderer content={content} images={images} />
          </PopoverContent>
        </Popover>
      )
    }

    if (type === "hovercard") {
      const { text, content } = props
      return (
        <HoverCard>
          <HoverCardTrigger asChild>
            <span className="cursor-pointer border-b border-dotted border-primary text-primary">{text}</span>
          </HoverCardTrigger>
          <HoverCardContent className="w-80">
            <MarkdownRenderer content={content} images={images} />
          </HoverCardContent>
        </HoverCard>
      )
    }

    if (type === "carousel") {
      const { images, width, height } = props

      // Check if we have any images
      if (!images || images.length === 0) {
        return <div className="my-6 p-4 border rounded bg-muted">No images found for carousel</div>
      }

      return (
        <div className={`my-6 mx-auto`} style={{
          maxWidth: `${width}px`
        }}>
          <Carousel opts={{
            loop: true,
            align: "center"
          }}>
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div className={`overflow-hidden rounded-lg border w-max`}>
                    <Image
                      src={image.src || "/placeholder.svg"}
                      alt={image.alt || "Image"}
                      width={width}
                      height={height}
                      className={`w-full object-cover`}
                      style={{ maxHeight: `${height}px`, maxWidth: `${width}px` }}
                    />
                    {image.alt && (
                      <div className="bg-muted p-2 text-center text-sm text-muted-foreground">{image.alt}</div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      )
    }

    if (type === "card") {
      const { title, description, content } = props
      return (
        <Card className="my-6">
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </CardHeader>
          <CardContent>
            <MarkdownRenderer content={content} images={images} />
          </CardContent>
        </Card>
      )
    }

    return null
  }

  // Custom components for markdown
  const components = {
    h1: ({ node, ...props }) => <h1 className="mt-8 scroll-m-20 text-4xl font-bold tracking-tight" {...props} />,
    h2: ({ node, ...props }) => (
      <h2 className="mt-8 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight" {...props} />
    ),
    h3: ({ node, ...props }) => <h3 className="mt-6 scroll-m-20 text-2xl font-semibold tracking-tight" {...props} />,
    h4: ({ node, ...props }) => <h4 className="mt-4 scroll-m-20 text-xl font-semibold tracking-tight" {...props} />,
    h5: ({ node, ...props }) => <h5 className="mt-4 scroll-m-20 text-lg font-semibold tracking-tight" {...props} />,
    h6: ({ node, ...props }) => <h6 className="mt-4 scroll-m-20 text-base font-semibold tracking-tight" {...props} />,
    p: ({ node, children, ...props }) => {
      const renderNested = ({ children, id, ids }: { children: string, id: string, ids: string[] }) => {
        const parts = children.split(id)
        const component = customComponents.find((c) => c.id === id)
        const index = ids.findIndex(i => i === id) + 1
        return (
          <>
            {parts[0]}
            {renderCustomComponent(component)}
            { parts[1].includes("CUSTOM_COMPONENT_") && index < ids.length ? renderNested({ children: parts[1], ids, id: ids[index] }) : parts[1] }
          </>
        )
      }

      // Check if this paragraph contains a custom component placeholder
      if (
        typeof children === "string" &&
        children.includes("CUSTOM_COMPONENT_")
      ) {
        const componentIds = children.match(/CUSTOM_COMPONENT_(\d+)/g) ?? []
        return (
          <div className="leading-7 not-first:mt-6" {...props}>
            {renderNested({ children, id: componentIds[0], ids: componentIds })}
          </div>
        )
      }
      return (
        <div className="leading-7 not-first:mt-6" {...props}>
          {children}
        </div>
      )
    },
    a: ({ node, href, ...props }) => {
      const [open, setOpen] = useState(false)
      // Check if it's an internal or external link
      if (href?.startsWith("http")) {
        return (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild onClick={() => setOpen(true)}>
              <a className="font-medium text-blue-400 underline underline-offset-4 cursor-pointer" {...props} />
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>External Link</DialogTitle>
                <DialogDescription>
                  You are about to visit an external website. Do you want to continue?
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button asChild onClick={() => setOpen(false)}>
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Continue
                  </a>
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )
      } else {
        // Internal link
        return <Link href={href} className="font-medium text-blue-400 underline underline-offset-4" {...props} />
      }
    },
    img: ({ node, src, alt, ...props }) => {
      // Find the image in the images array if it's a reference
      const imageId = src?.startsWith("img:") ? src.substring(4) : null
      const image = imageId ? images.find((img) => img.id === imageId) : null

      const imageSrc = image ? image.url : src
      const imageAlt = alt || image?.alt?.en_us || "Image"

      const [width, setWidth] = useState(0)
      const [height, setHeight] = useState(0)

      useEffect(() => {
        const loadSize = async () => {
          const [w, h] = await imageSize(imageSrc)
          setWidth(w)
          setHeight(h)
        }
        loadSize()
      }, [imageSrc])

      return (
        <div className="flex justify-center">
          <div className="my-6 overflow-hidden rounded-lg border w-max h-max">
            <div className="relative w-max h-max">
              <Image
                src={imageSrc || "/placeholder.svg"}
                alt={imageAlt}
                width={width}
                height={height}
                className="object-cover"
              />
              <div className="absolute bottom-2 right-2 flex gap-2">
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm"
                  onClick={() => handleDownloadImage(imageSrc, imageAlt)}
                >
                  <Download className="h-4 w-4" />
                  <span className="sr-only">Download image</span>
                </Button>
              </div>
            </div>
            {imageAlt && imageAlt !== "Image" && (
              <div className="bg-muted p-2 text-center text-sm text-muted-foreground">{imageAlt}</div>
            )}
          </div>
        </div>
      )
    },
    pre: ({ node, ...props }) => <div className="my-6 overflow-hidden rounded-lg" {...props} />,
    code: ({ node, inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || "")
      const code = String(children).replace(/\n$/, "")

      // This is the key fix for inline code
      if (!className) {
        return (
          <code
            className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold"
            {...props}
          >
            {children}
          </code>
        )
      }

      const language = match ? match[1] : "text"

      return (
        <div className="relative">
          {/* Language label */}
          <div className="absolute left-4 top-2 z-10">
            <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">{language}</span>
          </div>
          <div className="absolute right-2 top-2 z-10 flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm"
              onClick={() => handleCopyCode(code)}
            >
              {copiedCode === code ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="sr-only">Copy code</span>
            </Button>
          </div>
          <SyntaxHighlighter
            language={match ? match[1] : "text"}
            style={oneDark}
            customStyle={{
              margin: 0,
              padding: "2.5rem 1rem 1rem 0.5rem",
              fontSize: "0.775rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word"
            }}
            showLineNumbers
            wrapLongLines
            {...props}
          >
            {code}
          </SyntaxHighlighter>
        </div>
      )
    },
    ul: ({ node, ...props }) => <ul className="my-6 ml-6 list-disc [&>li]:mt-2" {...props} />,
    ol: ({ node, ...props }) => <ol className="my-6 ml-6 list-decimal [&>li]:mt-2" {...props} />,
    li: ({ node, children, ...props }) => {
      const renderNested = ({ children, id, ids }: { children: string, id: string, ids: string[] }) => {
        const parts = children.split(id)
        const component = customComponents.find((c) => c.id === id)
        const index = ids.findIndex(i => i === id) + 1
        return (
          <>
            {parts[0]}
            {renderCustomComponent(component)}
            { parts[1].includes("CUSTOM_COMPONENT_") && index < ids.length ? renderNested({ children: parts[1], ids, id: ids[index] }) : parts[1] }
          </>
        )
      }

      // Check if this paragraph contains a custom component placeholder
      if (
        typeof children === "string" &&
        children.includes("CUSTOM_COMPONENT_")
      ) {
        const componentIds = children.match(/CUSTOM_COMPONENT_(\d+)/g) ?? []
        return (
          <li className="leading-7 not-first:mt-6" {...props}>
            {renderNested({ children, id: componentIds[0], ids: componentIds })}
          </li>
        )
      }
      return (
        <li className="leading-7 not-first:mt-6" {...props}>
          {children}
        </li>
      )
    },
    blockquote: ({ node, ...props }) => <blockquote className="mt-6 border-l-2 pl-6 italic" {...props} />,
    hr: ({ node, ...props }) => <Separator className="my-6" {...props} />,
    table: ({ node, ...props }) => (
      <div className="my-6 w-full overflow-auto">
        <Table className="w-full rounded-md md-table" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => <TableHeader className="rounded-md bg-muted mb-1 border-0" {...props} />,
    tbody: ({ node, ...props }) => <TableBody className="rounded-md" {...props} />,
    tr: ({ node, ...props }) => <TableRow className="m-0 p-0 rounded-md" {...props} />,
    th: ({ node, ...props }) => (
      <TableHead
        className="first:rounded-l-md last:rounded-r-md border-0 px-4 py-2 text-left font-bold [[align=center]]:text-center [[align=right]]:text-right"
        {...props}
      />
    ),
    td: ({ node, ...props }) => (
      <TableCell className="border-0 px-4 py-2 text-left [[align=center]]:text-center [[align=right]]:text-right" {...props} />
    ),
  }

  return (
    <>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {processedContent}
      </ReactMarkdown>

      {/* External Link Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>External Link</DialogTitle>
            <DialogDescription>You are about to visit an external website. Do you want to continue?</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button asChild>
              <a href={dialogUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Continue
              </a>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
